import { CellContext, Column, createColumnHelper } from '@tanstack/react-table';
import { CheckCircle, XCircle } from 'lucide-react';

import Podman from '@/assets/ico/vendor/podman.svg?c';
import Docker from '@/assets/ico/vendor/docker.svg?c';
import Kubernetes from '@/assets/ico/vendor/kubernetes.svg?c';
import { getValueAsArrayOfStrings } from '@/portainer/helpers/array';

import { Badge, BadgeType } from '@@/Badge';
import { Link } from '@@/Link';
import { MultipleSelectionFilter } from '@@/datatables/Filter';
import { Icon } from '@@/Icon';
import { Tooltip } from '@@/Tip/Tooltip';
import { badgeTypeClasses } from '@@/Badge/Badge';
import { ProgressBar } from '@@/ProgressBar';

import {
  PolicyType,
  getPolicyTypeDisplayName,
  getPolicyCategory,
  getEnvironmentTypeDisplayName,
} from '../../types';

import { EnrichedPolicy } from './types';

const columnHelper = createColumnHelper<EnrichedPolicy>();

export const columns = [
  columnHelper.accessor('Name', {
    header: 'Name',
    cell: NameCell,
  }),
  columnHelper.accessor('EnvironmentType', {
    header: 'Environment',
    cell: EnvironmentCell,
    enableColumnFilter: true,
    filterFn: ({ original: policy }, columnId, filterValue: Array<string>) => {
      if (filterValue.length === 0) {
        return true;
      }

      const displayName = getEnvironmentTypeDisplayName(policy.EnvironmentType);
      return filterValue.includes(displayName);
    },
    meta: {
      filter: EnvironmentFilter,
    },
  }),
  columnHelper.accessor('Type', {
    header: 'Type',
    cell: TypeCell,
    enableColumnFilter: true,
    filterFn: ({ original: policy }, columnId, filterValue: Array<string>) => {
      if (filterValue.length === 0) {
        return true;
      }

      const displayName = getPolicyTypeDisplayName(policy.Type);
      return filterValue.includes(displayName);
    },
    meta: {
      filter: TypeFilter,
    },
  }),
  columnHelper.accessor(
    (row) => row.environmentGroups.map((group) => group.name),
    {
      id: 'attachedTo',
      header: 'Environment groups attached',
      cell: (props) => (
        <AttachedToCell
          environmentGroups={props.row.original.environmentGroups}
        />
      ),
    }
  ),
  columnHelper.accessor((row) => row.status, {
    id: 'status',
    header: 'Status',
    cell: StatusCell,
  }),
  // Last updated timestamp
  columnHelper.accessor('UpdatedAt', {
    header: 'Last updated',
    cell: LastUpdatedCell,
  }),
];

function NameCell({ getValue, row }: CellContext<EnrichedPolicy, string>) {
  const name = getValue();
  const policy = row.original;

  return (
    <Link
      to="portainer.policy"
      params={{ id: policy.Id }}
      data-cy={`policy-link-${name}`}
    >
      {name}
    </Link>
  );
}

function LastUpdatedCell({ getValue }: CellContext<EnrichedPolicy, string>) {
  const updatedAt = getValue();
  // UpdatedAt comes from the API as a string timestamp; fallback if missing
  if (!updatedAt) {
    return <span className="text-muted">Unknown</span>;
  }

  let formatted: string;
  try {
    const date = new Date(updatedAt);
    formatted = Number.isNaN(date.getTime())
      ? 'Unknown'
      : date.toLocaleString();
  } catch (e) {
    formatted = 'Unknown';
  }

  return <span>{formatted}</span>;
}

function EnvironmentCell({
  getValue,
}: CellContext<EnrichedPolicy, EnrichedPolicy['EnvironmentType']>) {
  const environmentType = getValue();

  function getIcon() {
    switch (environmentType) {
      case 'kubernetes':
        return Kubernetes;
      case 'podman':
        return Podman;
      case 'docker':
      case 'swarm':
      default:
        return Docker;
    }
  }

  const Icon = getIcon();
  const environmentDisplayName = getEnvironmentTypeDisplayName(environmentType);

  return (
    <div className="flex items-center">
      <Icon className="h-4 w-4" />
      <span>{environmentDisplayName}</span>
    </div>
  );
}

function TypeCell({
  row: { original: policy },
}: CellContext<EnrichedPolicy, PolicyType>) {
  const category = getPolicyCategory(policy.Type);
  const displayName = getPolicyTypeDisplayName(policy.Type);

  const badgeType: BadgeType = category === 'rbac' ? 'success' : 'info';

  return <Badge type={badgeType}>{displayName}</Badge>;
}

function AttachedToCell({
  environmentGroups,
}: {
  environmentGroups: EnrichedPolicy['environmentGroups'];
}) {
  const maxBadges = 5;
  const shortenedBadgeValues = environmentGroups.slice(0, maxBadges);
  const extraBadges = environmentGroups.length - maxBadges;

  return (
    <div className="flex flex-wrap max-w-md gap-x-2 gap-y-1">
      {shortenedBadgeValues.map((group) => (
        <Badge
          type={group.status}
          key={group.name}
          className="flex items-center gap-1 min-w-max"
        >
          <Icon icon={group.status === 'info' ? CheckCircle : XCircle} />
          <Link
            to="portainer.groups.group"
            params={{ id: group.id }}
            data-cy={`environment-group-link-${group.name}`}
            className={badgeTypeClasses[group.status]}
          >
            {group.name}
          </Link>
          {group.statusMessage && (
            <Tooltip message={group.statusMessage} className="!ml-0" />
          )}
        </Badge>
      ))}
      {extraBadges > 0 && (
        <Badge type="muted" className="flex items-center gap-1 min-w-max">
          + {extraBadges} more
        </Badge>
      )}
    </div>
  );
}

function StatusCell({
  row,
}: CellContext<EnrichedPolicy, EnrichedPolicy['status']>) {
  const policy = row.original;

  const status = policy.status
    ? { applied: 1, acknowledged: 0, failed: 0, total: 1 }
    : { applied: 0, acknowledged: 0, failed: 1, total: 1 };
  return (
    <ProgressBar
      className="!w-48"
      steps={[
        {
          value: status.applied,
          color: '#27ae60',
          className: 'bg-success-7',
        },
        {
          value: status.acknowledged,
          color: '#f39c12',
          className: 'bg-warn-7',
        },
        {
          value: status.failed,
          color: '#e74c3c',
          className: 'bg-danger-7',
        },
      ]}
      total={status.total}
    />
  );
}

const environmentFilterOptions = [
  'Kubernetes',
  'Docker',
  'Podman',
  'Docker Swarm',
] as const;

function EnvironmentFilter({
  column: { getFilterValue, setFilterValue, id },
}: {
  column: Column<EnrichedPolicy>;
}) {
  const value = getFilterValue();
  const valueAsArray = getValueAsArrayOfStrings(value);

  return (
    <MultipleSelectionFilter
      options={environmentFilterOptions}
      filterKey={id}
      value={valueAsArray}
      onChange={setFilterValue}
      menuTitle="Filter by environment"
    />
  );
}

const typeFilterOptions = ['RBAC', 'Security', 'Registry', 'Setup'] as const;

function TypeFilter({
  column: { getFilterValue, setFilterValue, id },
}: {
  column: Column<EnrichedPolicy>;
}) {
  const value = getFilterValue();
  const valueAsArray = getValueAsArrayOfStrings(value);

  return (
    <MultipleSelectionFilter
      options={typeFilterOptions}
      filterKey={id}
      value={valueAsArray}
      onChange={setFilterValue}
      menuTitle="Filter by type"
    />
  );
}
